import { Component } from '@theme/component';

/** Native announcement component with gentle, accessible automatic playback. */
export class AnnouncementBar extends Component {
  #current = 0;
  #interval;
  #events;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#events = new AbortController();
    const { signal } = this.#events;
    this.addEventListener('mouseenter', this.suspend, { signal });
    this.addEventListener('mouseleave', this.resume, { signal });
    this.addEventListener('focusin', this.suspend, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    this.#motion.addEventListener('change', this.#handleMotionChange, { signal });
    this.current = 0;
    if (this.#motion.matches) this.pause();
    else this.play();
  }

  disconnectedCallback() {
    this.suspend();
    this.#events?.abort();
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.current = this.#current;
    this.suspend();
    if (!this.paused) this.play();
  }

  next() { this.current += 1; }
  previous() { this.current -= 1; }

  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;
    this.suspend();
    this.paused = false;
    if (document.hidden || this.matches(':hover') || this.contains(document.activeElement)) return;
    this.#interval = setInterval(() => this.next(), interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  togglePlayback() {
    if (this.paused) this.play();
    else this.pause();
  }

  get paused() { return this.hasAttribute('paused'); }
  set paused(value) {
    this.toggleAttribute('paused', value);
    const control = this.querySelector('.announcement-bar__playback');
    if (control) control.setAttribute('aria-label', value ? control.dataset.playLabel : control.dataset.pauseLabel);
  }

  suspend = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
  };

  resume = () => {
    if (!this.autoplay || this.paused) return;
    this.play();
  };

  get autoplay() { return Boolean(this.autoplayInterval); }
  get autoplayInterval() {
    const value = Number.parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isFinite(value) && value > 0 ? value * 1000 : undefined;
  }

  get current() { return this.#current; }
  set current(value) {
    const slides = this.refs.slides ?? [];
    if (!slides.length) return;
    this.#current = ((value % slides.length) + slides.length) % slides.length;
    slides.forEach((slide, index) => {
      const hidden = index !== this.#current;
      slide.setAttribute('aria-hidden', String(hidden));
      slide.toggleAttribute('inert', hidden);
    });
  }

  #handleFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) this.resume();
  };
  #handleVisibilityChange = () => document.hidden ? this.suspend() : this.resume();
  #handleMotionChange = () => { if (this.#motion.matches) this.pause(); };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
